import { cn } from 'lib/utils'
import * as React from 'react'

interface DraggableCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** True while dnd-kit reports this card is being dragged. Dims it in place; the drag overlay carries the visual. */
  isDragging?: boolean
  /** True while a draggable hovers a droppable that this card represents. Lights up the acid border. */
  isOver?: boolean
  /** True if the slot accepts the current drop. Used together with `isOver` for valid/invalid affordance. */
  canDrop?: boolean
  children: React.ReactNode
}

/**
 * Styled wrapper for dnd-kit draggable / droppable cards. Owns the drag
 * affordance (acid border + halo on drag-over, dim while dragging) so every
 * surface that uses dnd-kit speaks the same visual language.
 *
 * The drag wiring (`useDraggable`, `useDroppable`, sensors, transforms) stays
 * with the consumer — each surface owns its own drag identity. This wrapper
 * just paints the right state.
 *
 * Used by: CalendarDayCell (drop target + drag source), Cockpit DayTile
 * (planned drop target).
 *
 * Spec: playbook/design-system/components.md#draggable-card.
 *
 * Example:
 *
 *   const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable(...)
 *   const { setNodeRef: setDropRef, isOver } = useDroppable(...)
 *
 *   <DraggableCard
 *     ref={(node) => { setNodeRef(node); setDropRef(node) }}
 *     isDragging={isDragging}
 *     isOver={isOver}
 *     style={{ transform: CSS.Translate.toString(transform) }}
 *     {...attributes} {...listeners}
 *   >
 *     ...
 *   </DraggableCard>
 */
export const DraggableCard = React.forwardRef<HTMLDivElement, DraggableCardProps>(
  ({ isDragging, isOver, canDrop = true, className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-dragging={isDragging || undefined}
        data-over={isOver || undefined}
        data-can-drop={canDrop || undefined}
        className={cn(
          // Base — reads as a clickable T3 card, not as a chunky drag handle.
          'relative rounded-[12px] border border-line bg-bg-2 transition-colors',
          'cursor-grab touch-none active:cursor-grabbing',
          // Hover lift is restrained on draggables; the drag-over halo is
          // the louder affordance.
          'hover:border-line-bright',
          // Dragging — fade the source so the user understands the card is
          // "in flight"; the dnd-kit DragOverlay carries the visible card.
          'data-[dragging=true]:cursor-grabbing data-[dragging=true]:opacity-40',
          // Drag-over — acid border + soft halo on a valid drop target.
          // Matches `.select-card[data-active="true"]` and the cockpit's
          // approve-pulse, so the whole product speaks one drop language.
          'data-[over=true]:border-acid data-[over=true]:bg-[color:var(--acid-tint)] data-[over=true]:shadow-[0_0_0_3px_var(--acid-soft)]',
          // Invalid drop — the rare case (different content type, past day, etc.).
          // Use warning amber instead of acid so the user reads "no" by color.
          'data-[over=true]:data-[can-drop=false]:border-[color:var(--warning)] data-[over=true]:data-[can-drop=false]:bg-[color:color-mix(in_srgb,var(--warning)_8%,transparent)] data-[over=true]:data-[can-drop=false]:shadow-[0_0_0_3px_color-mix(in_srgb,var(--warning)_30%,transparent)]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
DraggableCard.displayName = 'DraggableCard'
